import type { BookingData } from '../types';

interface StepDefinition {
  id: string;
  name: string;
  component: string;
  next?: string | ((data: BookingData) => string | null);
  validate?: string;
  requiresAuth?: boolean;
  external?: {
    type: string;
    returnParam: string;
  };
}

interface ExternalServiceConfig {
  returnParam: string;
  successStatus: string;
  failureStatus: string;
}

interface FlowConfiguration {
  metadata: {
    version: string;
    name: string;
    description: string;
  };
  steps: Record<string, StepDefinition>;
  ui: {
    showProgressBar: boolean;
    allowBackNavigation: boolean;
    saveStateOnEachStep: boolean;
    showStepNumbers: boolean;
  };
  externalServices: {
    mitid: ExternalServiceConfig;
    payment: ExternalServiceConfig & {
      providers: string[];
    };
  };
  getStep: (stepId: string) => StepDefinition | undefined;
  getNextStep: (currentStepId: string, data: BookingData) => string | null;
  getValidator: (stepId: string) => string | null;
  requiresAuth: (stepId: string) => boolean;
  getStepOrder: () => string[];
}

export const FlowConfig: FlowConfiguration = {
  metadata: {
    version: '1.2',
    name: 'passport_application',
    description: 'Pas ansøgning flow'
  },

  steps: {
    start: { id: 'start', name: 'Introduktion', component: 'StartStep', next: 'ask_for_whom', requiresAuth: false },
    gdpr: { id: 'gdpr', name: 'GDPR', component: 'GdprStep', next: 'login', requiresAuth: false },
    login: { id: 'login', name: 'Log ind', component: 'LoginStep', next: 'ask_for_whom', requiresAuth: false },
    ask_for_whom: {
      id: 'ask_for_whom',
      name: 'Hvem er passet til?',
      component: 'AskForWhomStep',
      validate: 'ask_for_whom',
      next: (data: BookingData) => (data.forWhom === 'child' ? 'child_info' : 'ask_has_old_passport'),
      requiresAuth: false
    },
    child_info: { id: 'child_info', name: 'Pas til barn', component: 'ChildPassportInfoStep', next: 'custody', requiresAuth: false },
    custody: {
      id: 'custody',
      name: 'Forældremyndighed',
      component: 'CustodyStep',
      next: (data: BookingData) => (data.custodyBothPresent ? 'ask_has_old_passport' : 'medindehaver_details'),
      requiresAuth: false
    },
    medindehaver_details: { id: 'medindehaver_details', name: 'Medindehaver', component: 'MedindehaverDetailsStep', next: 'ask_has_old_passport', requiresAuth: false },
    ask_has_old_passport: {
      id: 'ask_has_old_passport',
      name: 'Har du et gyldigt pas?',
      component: 'AskHasOldPassportStep',
      validate: 'ask_has_old_passport',
      next: (data: BookingData) => (data.hasOldPassport ? 'ask_passport_damage' : 'ask_previous_citizenship'),
      requiresAuth: false
    },
    ask_passport_damage: {
      id: 'ask_passport_damage',
      name: 'Er passet beskadiget?',
      component: 'AskPassportDamageStep',
      validate: 'ask_passport_damage',
      next: (data: BookingData) => (data.passportDamage === 'lost' ? 'declaration_lost' : 'ask_height'),
      requiresAuth: false
    },
    declaration_lost: { id: 'declaration_lost', name: 'Bortkommet pas', component: 'DeclarationLostPassportStep', next: 'ask_height', requiresAuth: false },
    ask_previous_citizenship: {
      id: 'ask_previous_citizenship',
      name: 'Tidligere statsborgerskab',
      component: 'AskPreviousCitizenshipStep',
      validate: 'ask_previous_citizenship',
      next: (data: BookingData) => (data.previousCitizenship ? 'warn_documentation' : 'ask_height'),
      requiresAuth: false
    },
    warn_documentation: { id: 'warn_documentation', name: 'Dokumentation', component: 'WarnDocumentationStep', next: 'ask_height', requiresAuth: false },
    ask_height: { id: 'ask_height', name: 'H�jde', component: 'PassportHeightStep', validate: 'ask_height', next: 'photo_method', requiresAuth: false },
    photo_method: {
      id: 'photo_method',
      name: 'Foto',
      component: 'PhotoMethodStep',
      validate: 'photo_method',
      next: 'photo_info',
      requiresAuth: false
    },
    photo_info: { id: 'photo_info', name: 'Pasfoto', component: 'PhotoInfoStep', next: 'contact_info', requiresAuth: false },
    contact_info: {
      id: 'contact_info',
      name: 'Kontakt',
      component: 'ContactInfoStep',
      validate: 'contact_info',
      next: (data: BookingData) => (data.user?.hasAddressingName ? 'addressing_name' : 'select_time'),
      requiresAuth: false
    },
    addressing_name: { id: 'addressing_name', name: 'Adresseringsnavn', component: 'AddressingNameStep', next: 'select_time', requiresAuth: false },
    select_time: {
      id: 'select_time',
      name: 'Vælg tid',
      component: 'TimeSelectionStep',
      validate: 'select_time',
      next: 'summary',
      requiresAuth: false
    },
    summary: { id: 'summary', name: 'Oversigt', component: 'SummaryStep', next: 'sign', requiresAuth: false },
    sign: { id: 'sign', name: 'Signér', component: 'SignStep', next: 'payment', requiresAuth: false },
    payment: {
      id: 'payment',
      name: 'Betaling',
      component: 'PaymentStep',
      next: 'confirmation',
      requiresAuth: true,
      external: { type: 'payment', returnParam: 'return=payment' }
    },
    confirmation: { id: 'confirmation', name: 'Bekræftelse', component: 'ConfirmationStep', requiresAuth: false }
  },

  ui: {
    showProgressBar: true,
    allowBackNavigation: true,
    saveStateOnEachStep: true,
    showStepNumbers: true
  },

  externalServices: {
    mitid: {
      returnParam: 'return=mitid',
      successStatus: 'success',
      failureStatus: 'failed'
    },
    payment: {
      returnParam: 'return=payment',
      successStatus: 'success',
      failureStatus: 'failed',
      providers: ['nets', 'mobilepay']
    }
  },

  getStep(stepId: string): StepDefinition | undefined {
    return this.steps[stepId];
  },

  getNextStep(currentStepId: string, data: BookingData): string | null {
    const step = this.steps[currentStepId];
    if (!step) {
      return null;
    }
    if (typeof step.next === 'function') {
      return step.next(data);
    }
    return step.next ?? null;
  },

  getValidator(stepId: string): string | null {
    const step = this.steps[stepId];
    return step?.validate || null;
  },

  requiresAuth(stepId: string): boolean {
    const step = this.steps[stepId];
    return step?.requiresAuth || false;
  },

  getStepOrder(): string[] {
    return Object.keys(this.steps);
  }
};
